import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useSaveTenantChat360, useTenantChat360, useTestTenantChat360 } from "../../lib/api";
import { FieldError, FieldLabel, GhostButton, PrimaryButton, SecondaryButton, inputClass } from "./primitives";

// This brand's own Chat360 account: the WhatsApp number it sends from and
// the credentials for it. The password and API key are stored encrypted and
// never come back to the browser — the fields show whether one is saved and
// stay blank unless you are replacing it.
const BLANK = { client_number: "", login_email: "", base_url: "", login_password: "", task_api_key: "" };

export default function Chat360Editor({ tenant }) {
  const { data, isLoading } = useTenantChat360(tenant.id);
  const save = useSaveTenantChat360(tenant.id);
  const test = useTestTenantChat360(tenant.id);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  const baseline = useMemo(
    () => ({
      ...BLANK,
      client_number: data?.client_number || "",
      login_email: data?.login_email || "",
      base_url: data?.base_url || "",
    }),
    [data],
  );

  useEffect(() => {
    setForm(baseline);
  }, [baseline]);

  // A test result belongs to the brand it was run for, not to a later reload
  // of the same brand's row.
  useEffect(() => {
    setResult(null);
  }, [tenant.id]);

  if (isLoading) {
    return (
      <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors({});
    setResult(null);
  };
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const persist = async () => {
    const number = form.client_number.replace(/\D/g, "");
    if (form.client_number && !number) {
      setErrors({ client_number: "Digits only, including the country code." });
      return false;
    }
    try {
      await save.mutateAsync({
        client_number: number,
        base_url: form.base_url.trim(),
        login_email: form.login_email.trim(),
        // Left out unless being replaced, so a blank field never wipes a
        // stored secret.
        ...(form.login_password ? { login_password: form.login_password } : {}),
        ...(form.task_api_key ? { task_api_key: form.task_api_key } : {}),
      });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
      return false;
    }
  };

  const onSave = async () => {
    if (await persist()) toast.success("Chat360 account saved.");
  };

  // The test runs against what is stored, so unsaved edits are saved first —
  // otherwise the result would describe credentials that are no longer on
  // screen.
  const onTest = async () => {
    setResult(null);
    if (dirty && !(await persist())) return;
    try {
      setResult(await test.mutateAsync());
    } catch (e) {
      setResult({ ok: false, detail: e?.response?.data?.detail || "Could not reach Chat360." });
    }
  };

  const secretPlaceholder = (saved) => (saved ? "Saved · enter a new one to replace" : "");

  return (
    <div className="mt-8 max-w-[560px] border-t border-[#F0F1F3] pt-6">
      <h2 className="text-[16px] font-semibold text-[#111827]">Chat360 account</h2>

      {!data?.encryption_ready && (
        <p className="mt-2 text-[13px] text-[#B45309]">
          Credentials can&apos;t be saved until BRAND_SECRET_KEY is set on the server.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-5">
        <div>
          <FieldLabel>WhatsApp number</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.client_number}
            placeholder="918799952622"
            onChange={(e) => set({ client_number: e.target.value })}
            data-testid="chat360-number"
          />
          <FieldError>{errors.client_number}</FieldError>
        </div>

        <div>
          <FieldLabel>Login email</FieldLabel>
          <input
            type="email"
            className={inputClass}
            value={form.login_email}
            onChange={(e) => set({ login_email: e.target.value })}
            data-testid="chat360-email"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>Password</FieldLabel>
            <input
              type="password"
              className={inputClass}
              value={form.login_password}
              placeholder={secretPlaceholder(data?.has_password)}
              onChange={(e) => set({ login_password: e.target.value })}
              data-testid="chat360-password"
            />
          </div>
          <div>
            <FieldLabel>Task API key</FieldLabel>
            <input
              type="password"
              className={inputClass}
              value={form.task_api_key}
              placeholder={secretPlaceholder(data?.has_task_api_key)}
              onChange={(e) => set({ task_api_key: e.target.value })}
              data-testid="chat360-api-key"
            />
          </div>
        </div>

        <div>
          <FieldLabel>Base URL</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.base_url}
            placeholder="https://app.chat360.io"
            onChange={(e) => set({ base_url: e.target.value })}
          />
        </div>

        {result && (
          <div
            className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-[13px] text-[#4B5563] shadow-[0_0_0_1px_#E5E7EB]"
            data-testid="chat360-test-result"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${result.ok ? "bg-[#16A34A]" : "bg-[#DC2626]"}`} />
            {result.detail}
          </div>
        )}

        <div className="flex items-center gap-3">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save changes
          </PrimaryButton>
          <SecondaryButton onClick={onTest} disabled={test.isPending}>
            {test.isPending && <Loader2 className="animate-spin" size={14} />}
            Test connection
          </SecondaryButton>
          {dirty && <GhostButton onClick={() => setForm(baseline)}>Discard</GhostButton>}
        </div>
      </div>
    </div>
  );
}
